/**
 * Utility functions for JWT token management.
 */

/**
 * Decodes a JWT token and returns the expiry timestamp in milliseconds.
 * @param token - The JWT token string
 * @returns The expiry timestamp in milliseconds, or null if token is invalid
 */
export const getTokenExpiry = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000; // Convert to milliseconds
  } catch {
    return null;
  }
};

/**
 * Calculates when to refresh a token (at 80% of token lifetime).
 * @param expiry - Token expiry timestamp in milliseconds
 * @returns Timestamp when refresh should occur
 */
export const calculateRefreshTime = (expiry: number): number => {
  const now = Date.now();
  const timeUntilExpiry = expiry - now;
  // Refresh at 80% of token lifetime (20% buffer)
  return now + timeUntilExpiry * 0.8;
};

/**
 * Returns true if the token is expired or will expire within the safety window.
 * @param token - The JWT token string
 * @param bufferMs - Safety window in ms: treat token as expired this many ms before actual expiry
 * @returns true if token is invalid, missing expiry, or expires within bufferMs
 */
export const isTokenExpired = (
  token: string,
  bufferMs: number
): boolean => {
  const expiry = getTokenExpiry(token);
  if (expiry === null) return true;
  return Date.now() + bufferMs >= expiry;
};

/** Buffer (ms) for WebSocket URL gating: do not connect if token expires within this window. */
export const WS_TOKEN_VALIDITY_BUFFER_MS = 30 * 1000;

/**
 * Returns the given URL only if the access token is present and not expired (within buffer).
 * Use when building WebSocket URLs so we never connect with an expired JWT.
 *
 * @param access - Access token (from auth state)
 * @param url - Full WebSocket URL including token query param
 * @param bufferMs - Buffer in ms (default: WS_TOKEN_VALIDITY_BUFFER_MS)
 * @returns url if token is valid, otherwise null
 */
export function webSocketUrlIfTokenValid(
  access: string | null | undefined,
  url: string,
  bufferMs: number = WS_TOKEN_VALIDITY_BUFFER_MS
): string | null {
  if (!access) return null;
  if (isTokenExpired(access, bufferMs)) return null;
  return url;
}

/**
 * Gets a cookie value by name.
 * @param name - Cookie name
 * @returns Cookie value or null
 */
export const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(";").shift() || null;
  }
  return null;
};
